// Cost Report view for the Billing tab.
//
// A straight roll-up of the job's cost history lines — the transaction set
// the Transactions view grids (billing.js's txns memo) plus the never-billed
// cost types it leaves out (burden, payroll taxes, truck / rig pay — always
// their own Burden column here), so the total ties to the Forecast tab's
// cost. No invoice, markup or rate-sheet math in it. Every line lands in
// exactly one bucket:
//
//     Phase (cost code) → Cost type
//
// with billable, not billable and burden cost as three columns on the one
// row, and each bucket carries its line count, hours and total cost, with
// phase subtotals and a job grand total at the bottom. "Billable" is the
// line's billable flag off the Transactions view (auto non-billable cost
// codes and reversed vendor invoices, plus any per-line override) — a line
// already on a summary is billable by definition. The split is the Cost Plus
// Forecast's (see sideOf), so Not Billable here is its Non-Billable row.
//
// Cost only: this is the cost side of the job as JDE posted it, not what it
// bills at. Marked-up amounts live on the summaries — except By cost type,
// which reads as a profitability view: area (labor, per diem, equipment,
// third party) → cost type → detail (the person, unit or vendor), with
// cost, what can't be invoiced, revenue invoiced (off the invoices sent in
// DocuSign), revenue still to invoice, and the margin. billingProfit.js has the rules.
//
// Committed, ETC and EAC sit after Cost once the selected forecast version is
// in: open POs, the forecast's cost-to-go past them, and cost + both, off
// jobPlanByCode — the numbers the Cost Plus Forecast and Overview read. A
// cost code or cost type with cost to go but nothing posted yet gets its own
// row. They are whole-job figures, so the Billable / Not Billable chips
// blank them rather than foot them against half the cost.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAtom } from 'jotai';
import { View, Text, Pressable } from '../lib/react-native-compat';
import { pNum } from '../db/company';
import { invoiceClaims, profitTree } from './billingProfit';
import { Chip, OutlineButton } from './procurementFilters';
import { contractPreview } from './billingContract';
import { downloadCostReport } from './billingCostReportXlsx';
import { downloadProfitReport } from './billingProfitXlsx';
import { isVoltaGridJob } from '../helpers/voltaGrid';
import { voltaGridWbsFor } from '../db/wbsVoltaGrid';
import { fuelLineAmt } from './billingFuel';
import {
    jobUserDataAtom, jobOpenCommitmentsAtom, selectedVersionAtom, selectedWIPAtom, dataSourceVersionAtom, projectCompleteAtom, jobPhaseCodesAtom,
} from '../store/company';
import { jobPlanByCode } from '../helpers/jobBudgetEac';
import { versionStartFor } from '../helpers/fieldBonus';
import { COST_TYPE_DESCRIPTION } from '../helpers/procurementCategories';
import { weekEnding } from './billingTicketDocs';
import { weekLabel } from './billingWeekGroups';

const card = {
    backgroundColor: '#ffffff', borderRadius: 6, borderWidth: 1, borderColor: '#e2e8f0',
    paddingTop: 16, paddingBottom: 16, paddingLeft: 18, paddingRight: 18, marginBottom: 16,
};
const th = { fontSize: 11, fontWeight: '700', color: '#475569', paddingTop: 6, paddingBottom: 6, paddingLeft: 8, paddingRight: 8 };
const td = { fontSize: 12, color: '#1e293b', paddingTop: 6, paddingBottom: 6, paddingLeft: 8, paddingRight: 8 };

const money = (n) => {
    const v = pNum(n);
    const abs = Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return (v < 0 ? '-$' : '$') + abs;
};
const hours = (n) => (pNum(n) ? pNum(n).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '');

// Which column a line's cost lands in — the Cost Plus Forecast's split, so
// the two tabs' not-billable figures agree:
//   'burden' — a never-billed cost type (burden, payroll taxes, truck / rig
//              pay). Never a Billing line; the Cost Plus Forecast keeps it in
//              Labor, off its Non-Billable row ("not including burden").
//   'nb'     — held off the invoice on the Billing tab.
//   'bill'   — everything else, including a rental voucher whose unit bills
//              on its HeavyJob hours (`ruUnit`): it is off the invoice only
//              because its usage lines bill for it, so its cost is billable.
const sideOf = (t) => (t.neverBilled ? 'burden' : t.nb && !t.ruUnit ? 'nb' : 'bill');
const SIDE_KEY = { bill: 'billCost', nb: 'nbCost', burden: 'burdenCost' };

const BILL_FILTERS = ['All', 'Billable', 'Not Billable', 'Burden'];
// The grid's three levels: cost code → cost type → week ending, week ending
// → cost code → cost type, or area → cost type → detail (the
// profitability view).
const GROUP_BY = [{ k: 'code', label: 'By cost code' }, { k: 'week', label: 'By week' }, { k: 'type', label: 'By cost type' }];
const FILTER_SIDE = { Billable: 'bill', 'Not Billable': 'nb', Burden: 'burden' };

// Column widths, shared by the header and every row so the three levels line
// up. Billable, Not Billable and Burden are columns, not separate rows: a
// cost type lands once and splits across them, so the eye reads the mix per
// line instead of hunting for the rest of the set.
const W = {
    label: 300, cls: 110, lines: 60, hrs: 80, bill: 120, nb: 120, burden: 120, cost: 120, committed: 120, etc: 120, eac: 120,
    cant: 120, inv: 120, open: 120, rev: 120, margin: 120, pct: 80,
};
// A rule between what has posted and what is still to come.
const PLAN_EDGE = { borderLeftWidth: 1, borderColor: '#e2e8f0' };

const Cell = ({ w, style, children, right, bold, color }) => (
    <Text
        style={[
            td,
            { width: w, textAlign: right ? 'right' : 'left' },
            bold ? { fontWeight: '700' } : null,
            color ? { color } : null,
            style,
        ]}
    >
        {children}
    </Text>
);

// Committed / ETC / EAC on one row. Blank under the billable chips (see
// showPlan); a zero commitment or ETC stays blank like the billable columns,
// but EAC always prints — it is the row's cost when nothing is left to come.
const PlanCells = ({ n, show, bold, always }) => (
    <>
        <Cell w={W.committed} right bold={bold} style={PLAN_EDGE}>{show && (n.committed || always) ? money(n.committed) : ''}</Cell>
        <Cell w={W.etc} right bold={bold}>{show && (n.etc || always) ? money(n.etc) : ''}</Cell>
        <Cell w={W.eac} right bold={bold}>{show ? money(n.eac) : ''}</Cell>
    </>
);

// The profitability view's columns (By cost type). Headers carry their
// definitions as tooltips.
const PROFIT_COLS = [
    { k: 'cost', w: W.cost, label: 'COST', tip: 'Every cost line, as JDE posted it' },
    { k: 'cant', w: W.cant, label: "CAN'T INVOICE", tip: 'Cost that never bills: burden and payroll taxes, lines held off the invoice, labor cost outside 511 / 512 / 515' },
    { k: 'inv', w: W.inv, label: 'INVOICED', tip: 'Revenue on invoices sent in DocuSign (out for approval or approved), fuel surcharge included', edge: true },
    { k: 'open', w: W.open, label: 'TO BE INVOICED', tip: 'Billable lines no sent invoice covers yet, at their bill amount with fuel surcharge' },
    { k: 'revenue', w: W.rev, label: 'REVENUE', tip: 'Invoiced + to be invoiced' },
    { k: 'margin', w: W.margin, label: 'MARGIN', tip: 'Revenue − cost' },
    { k: 'pct', w: W.pct, label: 'MARGIN %', tip: 'Margin ÷ revenue' },
];
const LOSS = '#d13138';
const pctText = (p) => (p == null ? '' : `${(Math.round(p * 1000) / 10).toFixed(1)}%`);

// One row's profitability cells. A zero stays blank, as in the cost columns;
// the TOTAL row (`always`) prints every figure. A loss prints red, its minus
// sign kept.
const ProfitCells = ({ n, bold, always, color, ready = true }) => (
    <>
        {PROFIT_COLS.map((c) => {
            const v = n[c.k];
            const text = !ready && c.k !== 'cost' && c.k !== 'cant' ? ''
                : c.k === 'pct' ? pctText(v) : v || always ? money(v) : '';
            const loss = (c.k === 'margin' || c.k === 'pct') && v < 0;
            return (
                <Cell key={c.k} w={c.w} right bold={bold} color={loss ? LOSS : color} style={c.edge ? PLAN_EDGE : null}>
                    {text}
                </Cell>
            );
        })}
    </>
);

// The collapse affordance. A row that folds has to look like one before it
// is clicked, so the caret sits in its own fixed column at the head of the
// label, turns from ▸ to ▾ with the state, and picks up the link colour on
// hover along with the row itself.
const Caret = ({ open, hovered }) => (
    <Text
        style={{
            width: 14,
            fontSize: 10,
            lineHeight: 16,
            color: hovered ? '#2563eb' : '#64748b',
        }}
    >
        {open ? '▾' : '▸'}
    </Text>
);

const BillingReport = ({ txns, jobKey, jobName, client, userName, contractRules, fuelCtx, invoiceRows, invoicesReady = true }) => {
    const [billFilter, setBillFilter] = useState('All');
    // The formatted export runs off a dynamic import, so the button reports
    // itself busy rather than looking dead while ExcelJS loads.
    const [building, setBuilding] = useState(null);
    // Cost codes open on click — the tab lands on the phase subtotals, and a
    // big job's cost types would bury them.
    const [expanded, setExpanded] = useState({});
    const [groupBy, setGroupBy] = useState('code');

    // The filter row and the grid's header follow the page down as it
    // scrolls, the header snug under the filters, both parked at the grid's
    // last row (Reconciliation's pattern). position:sticky can't: it pins
    // against the nearest scroll container, and the Billing page's ScrollView
    // grows with its content while the page around it scrolls. So a
    // document-level scroll listener (capture — scroll events don't bubble)
    // translates both by however far they've gone past the top edge, reading
    // the applied offset back off each element.
    const barRef = useRef(null);
    const headRef = useRef(null);
    const syncRef = useRef(null);
    useEffect(() => {
        const shifted = (el) => {
            const m = /translateY\((-?[\d.]+)px\)/.exec(el.style.transform || '');
            return m ? parseFloat(m[1]) : 0;
        };
        const put = (el, y) => {
            if (shifted(el) === y) return;
            el.style.transform = y ? `translateY(${y}px)` : '';
            el.style.boxShadow = y ? '0 2px 4px rgba(15, 23, 42, 0.08)' : '';
        };
        const sync = () => {
            const bar = barRef.current;
            const head = headRef.current;
            if (!bar || !head) return;
            // The pinning edge: the nearest ancestor that genuinely scrolls,
            // else the viewport top (the window scrolling).
            let edge = 0;
            for (let p = bar.parentElement; p && p !== document.documentElement; p = p.parentElement) {
                const o = window.getComputedStyle(p).overflowY;
                if ((o === 'auto' || o === 'scroll') && p.scrollHeight > p.clientHeight + 1) {
                    edge = p.getBoundingClientRect().top;
                    break;
                }
            }
            const barH = bar.offsetHeight;
            const barTop = bar.getBoundingClientRect().top - shifted(bar);
            const headTop = head.getBoundingClientRect().top - shifted(head);
            const parkAt = Math.max(0, head.parentElement.offsetHeight - head.offsetHeight);
            const headY = Math.min(parkAt, Math.max(0, Math.round(edge + barH - headTop)));
            put(head, headY);
            put(bar, Math.max(0, Math.min(headY + Math.round(headTop - barTop) - barH, Math.round(edge - barTop))));
        };
        syncRef.current = sync;
        document.addEventListener('scroll', sync, true);
        window.addEventListener('resize', sync);
        return () => {
            syncRef.current = null;
            document.removeEventListener('scroll', sync, true);
            window.removeEventListener('resize', sync);
        };
    }, []);
    // Groups opening, closing or filtering move the grid's end under a
    // scrolled page — re-place the two after every render.
    useEffect(() => { if (syncRef.current) syncRef.current(); });

    // Budget and EAC for the exports, and Committed / ETC / EAC on the grid:
    // the selected forecast version's Day Zero + approved COs, and its open
    // commitments + ETC, the way the Overview's budget grid reads them. Null
    // until that version's data is in, so the columns stay off rather than
    // pass cost to date off as EAC.
    const [jobUserData] = useAtom(jobUserDataAtom);
    const [jobOpenCommitments] = useAtom(jobOpenCommitmentsAtom);
    const [selectedVersion] = useAtom(selectedVersionAtom);
    const [selectedWIP] = useAtom(selectedWIPAtom);
    const [versions] = useAtom(dataSourceVersionAtom);
    const [projectComplete] = useAtom(projectCompleteAtom);
    const [phaseCodes] = useAtom(jobPhaseCodesAtom);
    const plan = useMemo(() => {
        if (!selectedVersion || !Array.isArray(jobUserData) || !Array.isArray(jobOpenCommitments)) return null;
        const mine = jobUserData.filter((d) => String(d?.BU_ID || '').trim() === String(jobKey || '').trim());
        if (!mine.length) return null;
        return jobPlanByCode({
            userData: mine,
            version: selectedVersion,
            openCommitments: jobOpenCommitments,
            versionStart: versionStartFor(versions, selectedVersion, selectedWIP),
            projectComplete,
        });
    }, [jobUserData, jobOpenCommitments, selectedVersion, selectedWIP, versions, projectComplete, jobKey]);

    const rows = useMemo(
        () => (txns || []).filter((t) => billFilter === 'All' || sideOf(t) === FILTER_SIDE[billFilter]),
        [txns, billFilter],
    );
    // Cost to go isn't split billable / not billable, so it only stands
    // beside the whole job's cost.
    const showPlan = !!plan && billFilter === 'All';

    // What the contract did to this job's cost — every rule that matched, with
    // the clause behind it — for the export's Contract sheet. On screen it
    // lives in Rates & Rules' Billability Rules card, not here. Always the
    // full line set, not the filtered rows:
    // the point is the whole picture, and the Billable filter would hide the
    // very lines the rules moved.
    // Minus the never-billed cost types (burden, taxes …) — no contract rule
    // moved those, they were never going on an invoice to begin with.
    const contract = useMemo(
        () => contractPreview((txns || []).filter((t) => !t.neverBilled), contractRules, jobKey),
        [txns, contractRules, jobKey],
    );

    // Phase → cost type → billable. Descriptions come off the first line in
    // each bucket; every line in a cost code carries the same one.
    const tree = useMemo(() => {
        const byPhase = {};
        const zero = { lines: 0, hrs: 0, cost: 0, billCost: 0, nbCost: 0, burdenCost: 0, committed: 0, etc: 0 };
        const phaseOf = (cc, desc) => byPhase[cc] || (byPhase[cc] = { cc, desc: desc || '', ...zero, types: {} });
        const typeOf = (ph, key, init) => ph.types[key] || (ph.types[key] = { key, ct: key, ctDesc: '', cls: '', label: '', ...zero, ...init });
        rows.forEach((t) => {
            const ph = phaseOf(t.cc || '(no cost code)', t.ccDesc);
            if (!ph.desc && t.ccDesc) ph.desc = t.ccDesc;
            const row = typeOf(ph, t.ct || '(no cost type)', { ctDesc: t.ctDesc || '', cls: t.cls || '' });
            if (!row.ctDesc && t.ctDesc) row.ctDesc = t.ctDesc;
            const c = pNum(t.cost);
            row.lines += 1; row.hrs += pNum(t.hrs); row.cost += c;
            ph.lines += 1; ph.hrs += pNum(t.hrs); ph.cost += c;
            const k = SIDE_KEY[sideOf(t)];
            row[k] += c; ph[k] += c;
        });
        // Cost to go onto the same buckets. A code or cost type nothing has
        // posted to yet still carries it, so it gets a row: leaving it off
        // would understate the job's EAC.
        if (showPlan) {
            const ctDesc = {};
            (txns || []).forEach((t) => { if (t.ct && t.ctDesc && !ctDesc[t.ct]) ctDesc[t.ct] = t.ctDesc; });
            Object.entries(plan.codes || {}).forEach(([cc, n]) => {
                Object.entries(n.types || {}).forEach(([key, x]) => {
                    if (!x.committed && !x.etc) return;
                    // The open PO's description, else the phase code list's.
                    const desc = n.desc || String((phaseCodes || {})[cc] || '').trim();
                    const ph = phaseOf(cc, desc);
                    if (!ph.desc && desc) ph.desc = desc;
                    const row = typeOf(ph, key, {
                        ct: x.ct, label: x.label, cls: x.cls,
                        ctDesc: x.ct ? (ctDesc[x.ct] || x.desc || COST_TYPE_DESCRIPTION[x.ct] || '') : '',
                    });
                    row.committed += x.committed; row.etc += x.etc;
                    ph.committed += x.committed; ph.etc += x.etc;
                });
            });
        }
        const withEac = (n) => ({ ...n, eac: n.cost + n.committed + n.etc });
        return Object.values(byPhase)
            .sort((a, b) => String(a.cc).localeCompare(String(b.cc)))
            .map((ph) => withEac({
                ...ph,
                types: Object.values(ph.types).sort((a, b) => String(a.key).localeCompare(String(b.key))).map(withEac),
            }));
    }, [rows, txns, plan, showPlan, phaseCodes]);

    // The profitability view (By cost type): which lines the invoices sent
    // in DocuSign claim, then area → cost type → cost code. Invoices are
    // whole-job, so their adjustment rows only stand under All.
    const claims = useMemo(
        () => invoiceClaims(txns || [], invoicesReady ? invoiceRows : [], fuelCtx),
        [txns, invoiceRows, invoicesReady, fuelCtx],
    );
    const profit = useMemo(
        () => (groupBy === 'type' ? profitTree(rows, claims, { withAdj: billFilter === 'All' }) : null),
        [groupBy, rows, claims, billFilter],
    );

    // What the grid draws for the picked view. By cost code it is the tree
    // above; by week it is week ending (newest first, undated lines last) →
    // cost code; by cost type it is the profit tree. Committed / ETC / EAC
    // are by cost code only. The cost totals are the same lines every way.
    const groups = useMemo(() => {
        if (profit) return profit.groups;
        const zero = { lines: 0, hrs: 0, cost: 0, billCost: 0, nbCost: 0, burdenCost: 0 };
        const add = (n, t) => {
            const c = pNum(t.cost);
            n.lines += 1; n.hrs += pNum(t.hrs); n.cost += c; n[SIDE_KEY[sideOf(t)]] += c;
        };
        const newestFirst = (a, b) => (!a.we ? 1 : !b.we ? -1 : b.we.localeCompare(a.we));
        const byKey = (a, b) => String(a.key).localeCompare(String(b.key));
        if (groupBy === 'code') {
            // Week endings under each cost type, off the posted lines; the
            // cost-to-go rows have no week, so a type with only ETC has none.
            const weeks = {};
            rows.forEach((t) => {
                const k = (t.cc || '(no cost code)') + '\u0000' + (t.ct || '(no cost type)');
                const we = weekEnding(t.date);
                const m = weeks[k] || (weeks[k] = {});
                add(m[we] || (m[we] = { key: 'we' + we, we, label: weekLabel(we), cls: '', ...zero }), t);
            });
            return tree.map((ph) => ({
                ...ph,
                id: ph.cc,
                label: ph.cc + (ph.desc ? ' — ' + ph.desc : ''),
                kids: ph.types.map((r) => ({
                    ...r,
                    label: r.ct ? r.ct + (r.ctDesc ? ' — ' + r.ctDesc : '') : r.label,
                    kids: Object.values(weeks[ph.cc + '\u0000' + r.key] || {}).sort(newestFirst),
                })),
            }));
        }
        const byWe = {};
        rows.forEach((t) => {
            const we = weekEnding(t.date);
            const w = byWe[we] || (byWe[we] = { id: 'wk' + we, we, ...zero, codes: {} });
            const cc = t.cc || '(no cost code)';
            const r = w.codes[cc] || (w.codes[cc] = { key: cc, cc, desc: '', cls: '', ...zero, types: {} });
            if (!r.desc && t.ccDesc) r.desc = t.ccDesc;
            const ct = t.ct || '(no cost type)';
            const x = r.types[ct] || (r.types[ct] = { key: ct, ct, ctDesc: t.ctDesc || '', cls: t.cls || '', ...zero });
            if (!x.ctDesc && t.ctDesc) x.ctDesc = t.ctDesc;
            [w, r, x].forEach((n) => add(n, t));
        });
        return Object.values(byWe)
            .sort(newestFirst)
            .map((w) => ({
                ...w,
                label: weekLabel(w.we),
                kids: Object.values(w.codes)
                    .sort(byKey)
                    .map((r) => ({
                        ...r,
                        label: r.cc + (r.desc ? ' — ' + r.desc : ''),
                        kids: Object.values(r.types)
                            .sort(byKey)
                            .map((x) => ({ ...x, label: x.ct + (x.ctDesc ? ' — ' + x.ctDesc : '') })),
                    })),
            }));
    }, [groupBy, tree, rows, profit]);
    const byWeek = groupBy === 'week';
    const byType = groupBy === 'type';
    const kidNoun = byWeek ? 'cost code' : byType ? 'cost type' : 'cost type';
    // What the third level holds; its rows are the same shape as the second.
    const leafNoun = byWeek ? 'cost type' : byType ? 'detail' : 'week';
    const groupNoun = byWeek ? 'weeks' : byType ? 'areas' : 'cost codes';
    const planCols = !!plan && groupBy === 'code';

    // The Expand/Collapse-all link reads the real state, not just whether the
    // map has keys in it: collapsing one phase after an expand-all leaves a
    // `false` behind, and the link would keep saying "Collapse all".
    const allCollapsed = groups.length > 0 && groups.every((g) => !expanded[g.id]);

    const total = useMemo(() => tree.reduce((a, p) => ({
        lines: a.lines + p.lines, hrs: a.hrs + p.hrs, cost: a.cost + p.cost,
        billCost: a.billCost + p.billCost, nbCost: a.nbCost + p.nbCost, burdenCost: a.burdenCost + p.burdenCost,
        committed: a.committed + p.committed, etc: a.etc + p.etc, eac: a.eac + p.eac,
    }), { lines: 0, hrs: 0, cost: 0, billCost: 0, nbCost: 0, burdenCost: 0, committed: 0, etc: 0, eac: 0 }), [tree]);

    // The two cost reports. Same workbook, same math, two audiences:
    //
    //   Client — billable cost only, the file that goes outside.
    //   Total  — every line, with billable and non-billable as their own
    //            columns and every backup row tagged, marked INTERNAL on
    //            its face and in its filename.
    //
    // Always the whole job, never the Billable/Not Billable chip: each cut
    // states its own scope in its title, and a filtered export would quietly
    // ship a partial total under a full-total heading.
    const exportReport = async (mode) => {
        setBuilding(mode);
        try {
            // The VoltaGrid jobs report on the client's own WBS (section →
            // group), and the client cut stops at the group level.
            const wbs = isVoltaGridJob(jobKey) ? voltaGridWbsFor : null;
            // The client cut states bill amounts, so each equipment line
            // carries its share of the fuel surcharge the summaries add on top.
            const adder = (t) => fuelLineAmt(t, fuelCtx || {});
            await downloadCostReport(txns || [], { jobKey, jobName, client, userName, contract, mode, wbs, adder, plan });
        } catch (e) {
            console.error('Cost report export failed:', e);
        } finally {
            setBuilding(null);
        }
    };

    // By cost type's own export: cost and revenue with each line's math, the
    // whole job whatever chip is on (the invoices are whole-job). Waits for
    // the invoices, or every line would read as still to invoice.
    const exportProfit = async () => {
        setBuilding('profit');
        try {
            await downloadProfitReport(txns || [], claims, fuelCtx, { jobKey, jobName, client, userName });
        } catch (e) {
            console.error('Cost & revenue export failed:', e);
        } finally {
            setBuilding(null);
        }
    };

    // The client cut states bill amounts, and ST/OT labor the rate sheet can't
    // price bills nothing — so it would sit in that file at $0. Said here,
    // before the export, rather than found by the client after it.
    const noRate = useMemo(() => {
        const rows = (txns || []).filter((t) => t.noRate);
        return { n: rows.length, cost: rows.reduce((a, t) => a + pNum(t.cost), 0) };
    }, [txns]);

    const Stat = ({ label, value, sub, tone, tip }) => (
        <View style={{ marginRight: 28, marginBottom: 4 }}>
            <Text style={{ fontSize: 10, fontWeight: '700', color: '#64748b', letterSpacing: 0.4 }} title={tip}>{label}</Text>
            <Text style={{ fontSize: 17, fontWeight: '700', color: tone || '#0f172a' }}>{value}</Text>
            {sub ? <Text style={{ fontSize: 11, color: '#94a3b8' }}>{sub}</Text> : null}
        </View>
    );

    return (
        <View style={card}>
            <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
                <Text style={{ fontSize: 14, fontWeight: '700', color: '#0f172a', marginRight: 16 }}>Cost Report</Text>
                <View style={{ flexGrow: 1 }} />
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    {byType ? (
                        <View style={{ marginRight: 8 }}>
                            <OutlineButton
                                label={building === 'profit' ? 'Building…' : invoicesReady ? 'Export Cost & Revenue' : 'Reading invoices…'}
                                onPress={exportProfit}
                                disabled={!!building || !invoicesReady || !(txns || []).length}
                            />
                        </View>
                    ) : null}
                    <View style={{ marginRight: 8 }}>
                        <OutlineButton
                            label={building === 'client' ? 'Building…' : 'Export Client Cost Report'}
                            onPress={() => exportReport('client')}
                            disabled={!!building || !total.billCost}
                        />
                    </View>
                    <OutlineButton
                        label={building === 'total' ? 'Building…' : 'Export Total Cost Report'}
                        onPress={() => exportReport('total')}
                        disabled={!!building || !tree.length}
                    />
                </View>
            </View>

            {noRate.n ? (
                <Text style={{ fontSize: 11, color: '#b45309', marginBottom: 8 }}>
                    {`${noRate.n} labor line${noRate.n === 1 ? '' : 's'} (${money(noRate.cost)} cost) ${noRate.n === 1 ? 'has' : 'have'} no rate and will show at $0 on the Client Cost Report, which states bill amounts. Map the classification on Rates & Rules first.`}
                </Text>
            ) : null}

            {/* Cost to date. Burden, committed, ETC and EAC live in the grid's
                columns and TOTAL row, not up here. */}
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'flex-start', marginBottom: 10 }}>
                <Stat
                    label="TOTAL COST"
                    value={money(total.cost)}
                    sub={`${total.lines.toLocaleString('en-US')} lines${total.hrs ? ` · ${hours(total.hrs)} hrs` : ''}`}
                />
                {profit ? (
                    // The profitability story: what it earns, how much of
                    // that is out the door, what never bills, and what's left.
                    <>
                        <Stat
                            label="REVENUE"
                            value={invoicesReady ? money(profit.total.revenue) : '…'}
                            sub={invoicesReady ? `${money(profit.total.inv)} invoiced · ${money(profit.total.open)} to be invoiced` : 'Reading the invoices in DocuSign'}
                            tip="Invoiced + to be invoiced"
                        />
                        <Stat label="CAN'T INVOICE" value={money(profit.total.cant)} tone="#475569" tip={PROFIT_COLS[1].tip} />
                        <Stat
                            label="MARGIN"
                            value={invoicesReady ? money(profit.total.margin) : '…'}
                            sub={invoicesReady ? pctText(profit.total.pct) : ''}
                            tone={invoicesReady && profit.total.margin < 0 ? LOSS : undefined}
                            tip="Revenue − cost"
                        />
                    </>
                ) : (
                    <>
                        <Stat label="BILLABLE" value={money(total.billCost)} tone="#166534" />
                        <Stat label="NOT BILLABLE" value={money(total.nbCost)} tone="#475569" tip="Held off the invoice" />
                    </>
                )}
            </View>

            {/* Pinned with the header row below — see syncRef. */}
            <div ref={barRef} style={{ position: 'relative', zIndex: 5, backgroundColor: '#ffffff', paddingTop: 6, marginTop: -6, paddingBottom: 4 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap' }}>
                {BILL_FILTERS.map((f) => (
                    <Chip key={f} label={f} active={billFilter === f} onPress={() => setBillFilter(f)} />
                ))}
                {profit && billFilter !== 'All' ? (
                    <Text style={{ fontSize: 11, color: '#64748b', marginLeft: 8 }}>
                        Invoice adjustments and credits show under All.
                    </Text>
                ) : plan && !showPlan && !byType ? (
                    <Text style={{ fontSize: 11, color: '#64748b', marginLeft: 8 }}>
                        Committed, ETC and EAC show under All.
                    </Text>
                ) : null}
                <View style={{ flexGrow: 1 }} />
                {GROUP_BY.map((g) => (
                    <Chip key={g.k} label={g.label} active={groupBy === g.k} onPress={() => setGroupBy(g.k)} />
                ))}
            </View>
            </div>

            {/* The grid in its own box, so the header parks at its last row. */}
            <div>
            <div ref={headRef} style={{ position: 'relative', zIndex: 4, backgroundColor: '#ffffff' }}>
            <View style={{ flexDirection: 'row', borderBottomWidth: 1, borderColor: '#e2e8f0' }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', width: W.label, paddingTop: 6, paddingBottom: 6, paddingLeft: 8, paddingRight: 8 }}>
                    <Pressable
                        onPress={() => setExpanded(allCollapsed ? Object.fromEntries(groups.map((g) => [g.id, true])) : {})}
                        disabled={!groups.length}
                        title={`${allCollapsed ? 'Expand' : 'Collapse'} all ${groupNoun}`}
                        style={{ width: 14 }}
                    >
                        {({ hovered }) => (
                            <Text
                                style={{
                                    fontSize: 10,
                                    lineHeight: 16,
                                    color: hovered && groups.length ? '#2563eb' : '#94a3b8',
                                    opacity: groups.length ? 1 : 0.4,
                                }}
                            >
                                {allCollapsed ? '▿' : '▹'}
                            </Text>
                        )}
                    </Pressable>
                    <Text style={[th, { paddingTop: 0, paddingBottom: 0, paddingLeft: 0, paddingRight: 0 }]}>{byWeek ? 'WEEK / COST CODE / COST TYPE' : byType ? 'AREA / COST TYPE / DETAIL' : 'COST CODE / COST TYPE / WEEK'}</Text>
                </View>
                {!byType ? <Text style={[th, { width: W.cls }]}>CLASS</Text> : null}
                <Text style={[th, { width: W.lines, textAlign: 'right' }]}>LINES</Text>
                <Text style={[th, { width: W.hrs, textAlign: 'right' }]}>HOURS</Text>
                {byType ? PROFIT_COLS.map((c) => (
                    <Text key={c.k} style={[th, c.edge ? PLAN_EDGE : null, { width: c.w, textAlign: 'right' }]} title={c.tip}>{c.label}</Text>
                )) : (
                    <>
                        <Text style={[th, { width: W.bill, textAlign: 'right' }]}>BILLABLE</Text>
                        <Text style={[th, { width: W.nb, textAlign: 'right' }]}>NOT BILLABLE</Text>
                        <Text style={[th, { width: W.burden, textAlign: 'right' }]} title="Burden, payroll taxes, truck and rig pay — never billed">BURDEN</Text>
                        <Text style={[th, { width: W.cost, textAlign: 'right' }]}>COST</Text>
                    </>
                )}
                {planCols ? (
                    <>
                        <Text style={[th, PLAN_EDGE, { width: W.committed, textAlign: 'right' }]} title="Open PO amounts">COMMITTED</Text>
                        <Text style={[th, { width: W.etc, textAlign: 'right' }]} title="The forecast's cost to go beyond open POs">ETC</Text>
                        <Text style={[th, { width: W.eac, textAlign: 'right' }]} title="Cost + committed + ETC">EAC</Text>
                    </>
                ) : null}
            </View>
            </div>

            {!groups.length ? (
                <Text style={{ fontSize: 12, color: '#64748b', paddingTop: 12, paddingBottom: 12 }}>
                    No cost history lines for this job.
                </Text>
            ) : null}

            {groups.map((p) => (
                <View key={p.id}>
                    <Pressable
                        disabled={!p.kids.length}
                        onPress={() => setExpanded((c) => ({ ...c, [p.id]: !c[p.id] }))}
                        style={({ hovered }) => ({
                            flexDirection: 'row',
                            alignItems: 'center',
                            backgroundColor: hovered ? '#e2e8f0' : '#f1f5f9',
                            borderBottomWidth: 1,
                            borderColor: '#e2e8f0',
                        })}
                        title={p.kids.length ? `${expanded[p.id] ? 'Hide' : 'Show'} ${kidNoun}s` : p.tip}
                    >
                        {({ hovered }) => (
                            <>
                                <View style={{ flexDirection: 'row', alignItems: 'center', width: W.label, paddingTop: 6, paddingBottom: 6, paddingLeft: 8, paddingRight: 8 }}>
                                    {p.kids.length ? <Caret open={!!expanded[p.id]} hovered={hovered} /> : <Text style={{ width: 14 }} />}
                                    <Text style={[td, { paddingTop: 0, paddingBottom: 0, paddingLeft: 0, paddingRight: 0, fontWeight: '700' }]}>
                                        {p.label}
                                    </Text>
                                </View>
                                {!byType ? (
                                    <Cell w={W.cls} color="#64748b">
                                        {expanded[p.id] ? '' : `${p.kids.length} ${kidNoun}${p.kids.length === 1 ? '' : 's'}`}
                                    </Cell>
                                ) : null}
                                <Cell w={W.lines} right bold>{p.lines || ''}</Cell>
                                <Cell w={W.hrs} right bold>{hours(p.hrs)}</Cell>
                                {byType ? <ProfitCells n={p} bold ready={invoicesReady} /> : (
                                    <>
                                        <Cell w={W.bill} right bold color="#166534">{p.billCost ? money(p.billCost) : ''}</Cell>
                                        <Cell w={W.nb} right bold color="#475569">{p.nbCost ? money(p.nbCost) : ''}</Cell>
                                        <Cell w={W.burden} right bold color="#475569">{p.burdenCost ? money(p.burdenCost) : ''}</Cell>
                                        <Cell w={W.cost} right bold>{p.lines ? money(p.cost) : ''}</Cell>
                                    </>
                                )}
                                {planCols ? <PlanCells n={p} show={showPlan} bold /> : null}
                            </>
                        )}
                    </Pressable>
                    {!expanded[p.id] ? null : p.kids.map((r) => {
                        // The second level folds too when it has anything
                        // under it: cost types under a week's cost code, or
                        // week endings under a cost type. Keyed under its
                        // parent so the same cost code in two weeks opens
                        // independently.
                        const rid = p.id + '|' + r.key;
                        const leaves = r.kids || [];
                        const open = !!expanded[rid];
                        return (
                            <View key={r.key}>
                                <Pressable
                                    disabled={!leaves.length}
                                    onPress={() => setExpanded((c) => ({ ...c, [rid]: !c[rid] }))}
                                    style={({ hovered }) => ({
                                        flexDirection: 'row',
                                        alignItems: 'center',
                                        backgroundColor: hovered && leaves.length ? '#f8fafc' : 'transparent',
                                        borderBottomWidth: 1,
                                        borderColor: '#f1f5f9',
                                    })}
                                    title={leaves.length ? `${open ? 'Hide' : 'Show'} ${leafNoun}s` : r.tip}
                                >
                                    {({ hovered }) => (
                                        <>
                                            <View style={{ flexDirection: 'row', alignItems: 'center', width: W.label, paddingTop: 6, paddingBottom: 6, paddingLeft: 30, paddingRight: 8 }}>
                                                {leaves.length ? <Caret open={open} hovered={hovered} /> : <Text style={{ width: 14 }} />}
                                                <Text style={[td, { paddingTop: 0, paddingBottom: 0, paddingLeft: 0, paddingRight: 0 }, r.adj ? { fontStyle: 'italic', color: '#525252' } : null]}>{r.label}</Text>
                                            </View>
                                            {!byType ? (
                                                <Cell w={W.cls} color={r.cls ? '#475569' : '#64748b'}>
                                                    {r.cls || (leaves.length && !open ? `${leaves.length} ${leafNoun}${leaves.length === 1 ? '' : 's'}` : '')}
                                                </Cell>
                                            ) : null}
                                            <Cell w={W.lines} right>{r.lines || ''}</Cell>
                                            <Cell w={W.hrs} right>{hours(r.hrs)}</Cell>
                                            {byType ? <ProfitCells n={r} ready={invoicesReady} color={r.adj ? '#525252' : undefined} /> : (
                                                <>
                                                    <Cell w={W.bill} right color="#166534">{r.billCost ? money(r.billCost) : ''}</Cell>
                                                    <Cell w={W.nb} right color="#475569">{r.nbCost ? money(r.nbCost) : ''}</Cell>
                                                    <Cell w={W.burden} right color="#475569">{r.burdenCost ? money(r.burdenCost) : ''}</Cell>
                                                    <Cell w={W.cost} right>{r.lines ? money(r.cost) : ''}</Cell>
                                                </>
                                            )}
                                            {planCols ? <PlanCells n={r} show={showPlan} /> : null}
                                        </>
                                    )}
                                </Pressable>
                                {!open ? null : leaves.map((x) => (
                                    <View
                                        key={x.key}
                                        style={{ flexDirection: 'row', alignItems: 'center', borderBottomWidth: 1, borderColor: '#f8fafc' }}
                                    >
                                        <Cell w={W.label} style={{ paddingLeft: 52 }} color="#475569">{x.label}</Cell>
                                        {!byType ? <Cell w={W.cls} color="#64748b">{x.cls}</Cell> : null}
                                        <Cell w={W.lines} right color="#475569">{x.lines || ''}</Cell>
                                        <Cell w={W.hrs} right color="#475569">{hours(x.hrs)}</Cell>
                                        {byType ? <ProfitCells n={x} ready={invoicesReady} color="#475569" /> : (
                                            <>
                                                <Cell w={W.bill} right color="#166534">{x.billCost ? money(x.billCost) : ''}</Cell>
                                                <Cell w={W.nb} right color="#475569">{x.nbCost ? money(x.nbCost) : ''}</Cell>
                                                <Cell w={W.burden} right color="#475569">{x.burdenCost ? money(x.burdenCost) : ''}</Cell>
                                                <Cell w={W.cost} right color="#475569">{x.lines ? money(x.cost) : ''}</Cell>
                                            </>
                                        )}
                                        {planCols ? <PlanCells n={x} show={false} /> : null}
                                    </View>
                                ))}
                            </View>
                        );
                    })}
                </View>
            ))}

            {groups.length ? (
                <View style={{ flexDirection: 'row', alignItems: 'center', borderTopWidth: 2, borderColor: '#0f172a' }}>
                    <Cell w={W.label} bold>TOTAL</Cell>
                    {!byType ? <Cell w={W.cls} /> : null}
                    <Cell w={W.lines} right bold>{total.lines}</Cell>
                    <Cell w={W.hrs} right bold>{hours(total.hrs)}</Cell>
                    {profit ? <ProfitCells n={profit.total} bold always ready={invoicesReady} /> : (
                        <>
                            <Cell w={W.bill} right bold color="#166534">{money(total.billCost)}</Cell>
                            <Cell w={W.nb} right bold color="#475569">{money(total.nbCost)}</Cell>
                            <Cell w={W.burden} right bold color="#475569">{money(total.burdenCost)}</Cell>
                            <Cell w={W.cost} right bold>{money(total.cost)}</Cell>
                        </>
                    )}
                    {planCols ? <PlanCells n={total} show={showPlan} bold always /> : null}
                </View>
            ) : null}

            {/* Billed but not revenue, and invoices the view couldn't read. */}
            {profit && invoicesReady && billFilter === 'All' ? (() => {
                const notes = [];
                if (Math.abs(profit.extras.tax) >= 0.005) notes.push(`Sales tax ${money(profit.extras.tax)} — collected for the state, so not revenue`);
                if (profit.unread.length) notes.push(`Not counted yet: ${profit.unread.join(', ')} (cover not read in DocuSign yet)`);
                return notes.length ? (
                    <View style={{ paddingTop: 8 }}>
                        <Text style={{ fontSize: 12, color: '#525252' }}>{`Also on the invoices: ${notes.join(' · ')}.`}</Text>
                    </View>
                ) : null;
            })() : null}
            </div>
        </View>
    );
};

export default BillingReport;
